import React, { useCallback, useEffect, useState } from "react";
import { getDprByDate, createDpr, updateDpr, listDprs } from "../api/dpr";
import { listWeather, upsertWeather } from "../api/weather";
import { listHsseq, upsertHsseq } from "../api/hsseq";
import {
  listLogEntries,
  createLogEntry,
  updateLogEntry,
  deleteLogEntry,
} from "../api/detailedLog";
import {
  listPersonnel,
  createPersonnel,
  updatePersonnel,
  deletePersonnel,
  copyPersonnelFromPreviousDpr,
} from "../api/personnel";
import { listTools, createTool, updateTool, deleteTool } from "../api/tools";
import { exportDprPdf } from "../lib/pdfExport";
import DprSummary from "./DprSummary";
import WeatherTable from "./WeatherTable";
import HsseqTable from "./HsseqTable";
import DetailedLogTable from "./DetailedLogTable";
import PersonnelTable from "./PersonnelTable";
import ToolTable from "./ToolTable";

const todayStr = () => new Date().toISOString().slice(0, 10);

const DprEditor = ({ projectId, userId, project, events }) => {
  const [reportDate, setReportDate] = useState(todayStr);
  const [dpr, setDpr] = useState(null);
  const [allDprs, setAllDprs] = useState([]);
  const [weather, setWeather] = useState([]);
  const [hsseq, setHsseq] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const loadDprData = useCallback(async (dprRecord) => {
    const [w, h, l, p, t] = await Promise.all([
      listWeather(dprRecord.id),
      listHsseq(dprRecord.id),
      listLogEntries(dprRecord.id),
      listPersonnel(dprRecord.id),
      listTools(dprRecord.id),
    ]);
    setWeather(w);
    setHsseq(h);
    setLogEntries(l);
    setPersonnel(p);
    setTools(t);
  }, []);

  const loadDpr = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [existing, dprs] = await Promise.all([
        getDprByDate(projectId, reportDate),
        listDprs(projectId),
      ]);
      setAllDprs(dprs);
      if (existing) {
        setDpr(existing);
        await loadDprData(existing);
      } else {
        setDpr(null);
        setWeather([]);
        setHsseq([]);
        setLogEntries([]);
        setPersonnel([]);
        setTools([]);
      }
    } catch (err) {
      setError(err.message || "Failed to load DPR");
    } finally {
      setLoading(false);
    }
  }, [projectId, reportDate, loadDprData]);

  useEffect(() => {
    loadDpr();
  }, [loadDpr]);

  const handleCreateDpr = async () => {
    setError("");
    try {
      const newDpr = await createDpr({ projectId, userId, reportDate });
      setDpr(newDpr);
      setAllDprs((prev) => [newDpr, ...prev]);
    } catch (err) {
      setError(err.message || "Failed to create DPR");
    }
  };

  const handleUpdateSummary = async (updated) => {
    try {
      const saved = await updateDpr(dpr.id, updated);
      setDpr(saved);
    } catch (err) {
      setError(err.message || "Failed to save summary");
    }
  };

  const handleSaveWeather = async (rows) => {
    try {
      const saved = await upsertWeather(rows.map((r) => ({ ...r, dprId: dpr.id })));
      setWeather(saved);
    } catch (err) {
      setError(err.message || "Failed to save weather");
    }
  };

  const handleSaveHsseq = async (rows) => {
    try {
      const saved = await upsertHsseq(rows.map((r) => ({ ...r, dprId: dpr.id })));
      setHsseq(saved);
    } catch (err) {
      setError(err.message || "Failed to save HSSEQ");
    }
  };

  const handleCreateLog = async (entry) => {
    try {
      const created = await createLogEntry(entry);
      setLogEntries((prev) =>
        [...prev, created].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        )
      );
    } catch (err) {
      setError(err.message || "Failed to add log entry");
    }
  };

  const handleUpdateLog = async (id, updates) => {
    try {
      const updated = await updateLogEntry(id, updates);
      setLogEntries((prev) =>
        prev
          .map((e) => (e.id === id ? updated : e))
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      );
    } catch (err) {
      setError(err.message || "Failed to update log entry");
    }
  };

  const handleDeleteLog = async (id) => {
    try {
      await deleteLogEntry(id);
      setLogEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete log entry");
    }
  };

  const handleCreatePersonnel = async (entry) => {
    try {
      const created = await createPersonnel(entry);
      setPersonnel((prev) => [...prev, created]);
    } catch (err) {
      setError(err.message || "Failed to add personnel");
    }
  };

  const handleUpdatePersonnel = async (id, entry) => {
    try {
      const updated = await updatePersonnel(id, entry);
      setPersonnel((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (err) {
      setError(err.message || "Failed to update personnel");
    }
  };

  const handleDeletePersonnel = async (id) => {
    try {
      await deletePersonnel(id);
      setPersonnel((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete personnel");
    }
  };

  const handleCopyPreviousPersonnel = async () => {
    const previousDpr = allDprs.find((d) => d.id !== dpr?.id);
    if (!previousDpr) {
      setError("No previous DPR found to copy from");
      return;
    }
    try {
      const copied = await copyPersonnelFromPreviousDpr(dpr.id, previousDpr.id);
      setPersonnel((prev) => [...prev, ...copied]);
    } catch (err) {
      setError(err.message || "Failed to copy personnel");
    }
  };

  const handleCreateTool = async (entry) => {
    try {
      const created = await createTool(entry);
      setTools((prev) => [...prev, created]);
    } catch (err) {
      setError(err.message || "Failed to add tool");
    }
  };

  const handleUpdateTool = async (id, entry) => {
    try {
      const updated = await updateTool(id, entry);
      setTools((prev) => prev.map((t) => (t.id === id ? updated : t)));
    } catch (err) {
      setError(err.message || "Failed to update tool");
    }
  };

  const handleDeleteTool = async (id) => {
    try {
      await deleteTool(id);
      setTools((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete tool");
    }
  };

  const handleExportPdf = async () => {
    if (!dpr) return;
    setExporting(true);
    setError("");
    try {
      await exportDprPdf({
        project,
        dpr,
        events,
        detailedLog: logEntries,
        weather,
        hsseq,
        personnel,
        tools,
      });
    } catch (err) {
      setError(err.message || "Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
        Loading DPR…
      </div>
    );
  }

  return (
    <section>
      <header className="mb-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <span className="section-eyebrow">Daily Progress Report</span>
        {dpr && (
          <span className="font-mono text-sm font-semibold text-navy-600">
            #{String(dpr.dprNumber).padStart(2, "0")}
          </span>
        )}
        <input
          type="date"
          value={reportDate}
          onChange={(e) => setReportDate(e.target.value)}
          className="input-mono w-auto"
        />
        {dpr && (
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exporting}
            className="btn-primary ml-auto"
          >
            {exporting ? "Generating PDF…" : "Export PDF"}
          </button>
        )}
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-700">
          {error}
        </div>
      )}

      {!dpr ? (
        <div className="rounded-md border border-dashed border-zinc-300 bg-white px-6 py-12 text-center">
          <p className="mb-1 text-sm text-zinc-600">
            No DPR for{" "}
            <span className="font-mono tabular-nums text-zinc-900">
              {reportDate}
            </span>
            .
          </p>
          <p className="mb-5 text-xs text-zinc-500">
            Create one to start logging weather, HSSEQ, personnel, and equipment.
          </p>
          <button
            type="button"
            onClick={handleCreateDpr}
            className="btn-primary"
          >
            Create DPR
          </button>
        </div>
      ) : (
        <div className="space-y-10">
          <DprSummary dpr={dpr} onUpdate={handleUpdateSummary} />
          <WeatherTable weather={weather} dprId={dpr.id} onSave={handleSaveWeather} />
          <HsseqTable hsseq={hsseq} dprId={dpr.id} onSave={handleSaveHsseq} />
          <DetailedLogTable
            logEntries={logEntries}
            timelineEvents={events}
            dprId={dpr.id}
            reportDate={reportDate}
            onCreate={handleCreateLog}
            onUpdate={handleUpdateLog}
            onDelete={handleDeleteLog}
          />
          <PersonnelTable
            personnel={personnel}
            dprId={dpr.id}
            onCreate={handleCreatePersonnel}
            onUpdate={handleUpdatePersonnel}
            onDelete={handleDeletePersonnel}
            onCopyPrevious={allDprs.length > 1 ? handleCopyPreviousPersonnel : null}
          />
          <ToolTable
            tools={tools}
            dprId={dpr.id}
            onCreate={handleCreateTool}
            onUpdate={handleUpdateTool}
            onDelete={handleDeleteTool}
          />
        </div>
      )}
    </section>
  );
};

export default DprEditor;
